/* GBOF cart analytics and recommendation-linked trust proof.
 *
 * Uses Shopify's custom customer-event API and the already-installed Triple Pixel
 * where available. No customer PII, address, email, payment or order data is
 * emitted. Standard Shopify add-to-cart and checkout events remain untouched.
 */
(() => {
  'use strict';

  const EVENT_PREFIX = 'gbof_cart:';
  const TRIPLE_PIXEL_EVENTS = {
    drawer_viewed: 'gbofCartDrawerViewed',
    upsell_viewed: 'gbofCartUpsellViewed',
    upsell_clicked: 'gbofCartUpsellClicked',
    upsell_added: 'gbofCartUpsellAdded',
    checkout_clicked: 'gbofCartCheckoutClicked'
  };
  const EVENT_LOG_KEY = '__GBOF_CART_ANALYTICS_EVENTS__';
  const MAX_EVENT_LOG = 80;

  const eventLog = window[EVENT_LOG_KEY] = window[EVENT_LOG_KEY] || [];
  let drawerOpenSequence = 0;
  let lastDrawerOpen = false;
  let observer;
  let pendingUpsell;
  let mobileScrollRefreshTimer;
  let cartPresentationRefreshTimer;
  let cartPresentationRefreshSequence = 0;

  function cleanText(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  function productHandleFromHref(href) {
    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (_) {
      return null;
    }
  }

  function cartContext(drawer) {
    const items = drawer ? drawer.querySelectorAll('cart-items .cart-item') : [];
    const recommendations = drawer ? drawer.querySelector('#cart-recommendations') : null;
    return {
      drawer_open_sequence: drawerOpenSequence,
      cart_item_count: items.length,
      recommendation_source_product_id: recommendationSourceProductId(recommendations)
    };
  }

  function recommendationSourceProductId(recommendation) {
    return recommendation?.dataset.gbofSourceProductId || recommendation?.dataset.productId || null;
  }

  function recommendationPosition(productCard) {
    if (!productCard) return null;
    const recommendationList = productCard.parentElement;
    if (!recommendationList) return null;
    const productCards = [...recommendationList.children]
      .filter((element) => element.matches('li') && !element.dataset.gbofTrustCard);
    const position = productCards.indexOf(productCard);
    return position === -1 ? null : position + 1;
  }

  function publish(name, payload) {
    const detail = {
      event: `${EVENT_PREFIX}${name}`,
      payload: payload || {},
      timestamp: new Date().toISOString()
    };

    // A small in-memory debug log makes staged validation possible; it is not
    // persisted or sent independently of Shopify's customer-event channel.
    eventLog.push(detail);
    if (eventLog.length > MAX_EVENT_LOG) eventLog.splice(0, eventLog.length - MAX_EVENT_LOG);

    try {
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(detail.event, detail.payload);
      }
    } catch (error) {
      // Analytics must never affect cart operation.
      console.warn('[GBOF cart analytics] custom event was not published', error);
    }

    // Triple Whale's documented Custom Event Builder uses the direct
    // TriplePixel('custom', eventName, data) interface. The corresponding five
    // event definitions are deliberately configured in the same naming scheme.
    // This is a safe no-op until the Triple Pixel has loaded on the page.
    const tripleEvent = TRIPLE_PIXEL_EVENTS[name];
    try {
      if (tripleEvent && Array.isArray(window.__GBOF_TRIPLE_PIXEL_TEST_EVENTS__)) {
        // Used only by the disposable visual validator; never set in a shopper's browser.
        window.__GBOF_TRIPLE_PIXEL_TEST_EVENTS__.push(['custom', tripleEvent, detail.payload]);
      } else if (tripleEvent && typeof window.TriplePixel === 'function') {
        window.TriplePixel('custom', tripleEvent, detail.payload);
      }
    } catch (error) {
      console.warn('[GBOF cart analytics] Triple Pixel custom event was not published', error);
    }

    window.dispatchEvent(new CustomEvent('gbof:cart-analytics', { detail }));
  }

  function refreshNativeMobileScrollLayer(drawer) {
    if (!window.matchMedia('(max-width: 600px)').matches || !drawer) return;
    const content = drawer.querySelector('.drawer__content');
    if (!content || content.scrollHeight <= content.clientHeight) return;

    // Shopify loads related products after the drawer is already open. On iOS,
    // a momentum scroller inside a fixed dialog can retain its old maximum
    // scroll range after that late content growth. This is intentionally a
    // one-off layout refresh, not a gesture handler: it never observes or
    // prevents touch events, so native thumb scrolling remains fully native.
    window.clearTimeout(mobileScrollRefreshTimer);
    mobileScrollRefreshTimer = window.setTimeout(() => {
      const savedScrollTop = content.scrollTop;
      content.style.webkitOverflowScrolling = 'auto';
      void content.offsetHeight;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        content.style.webkitOverflowScrolling = '';
        void content.offsetHeight;
        content.scrollTop = savedScrollTop;
        content.dataset.gbofNativeScrollRefreshed = 'true';
      }));
    }, 48);
  }

  function getCartDrawerSectionId(drawer) {
    const sectionId = drawer?.closest('.shopify-section')?.id || '';
    return sectionId.startsWith('shopify-section-') ? sectionId.slice('shopify-section-'.length) : null;
  }

  function cartItemSignature(drawer) {
    return [...(drawer?.querySelectorAll('cart-items .cart-item') || [])]
      .map((item) => {
        const variantId = item.dataset.variantId || '';
        const quantity = item.querySelector('.qty-input__input, input[name="updates[]"]')?.value || '';
        return `${variantId}:${quantity}`;
      })
      .join('|');
  }

  function refreshCartPresentation(drawer) {
    const sectionId = getCartDrawerSectionId(drawer);
    if (!drawer || !sectionId) return;

    // Canopy's native quantity flow replaces only cart-items and the footer.
    // The GBOF milestone, guarantee and trust panels intentionally sit outside
    // those fragments, so they need a small, post-change section refresh of
    // their own. This is presentation-only: it never changes the cart, price,
    // discounts, shipping profile, or checkout state.
    const sequence = ++cartPresentationRefreshSequence;
    window.clearTimeout(cartPresentationRefreshTimer);
    cartPresentationRefreshTimer = window.setTimeout(async () => {
      try {
        // Follow Canopy's relative-section request pattern. It preserves the
        // current page context and returns Shopify's sections JSON rather than
        // navigating a drawer shopper away from their product page.
        const response = await fetch(`?sections=${encodeURIComponent(sectionId)}`, {
          credentials: 'same-origin'
        });
        if (!response.ok) throw new Error(`Cart presentation response ${response.status}`);
        const sections = await response.json();
        if (sequence !== cartPresentationRefreshSequence || !drawer.isConnected) return;

        const html = sections?.[sectionId];
        if (typeof html !== 'string') throw new Error('Cart presentation section was not returned');
        const template = document.createElement('template');
        template.innerHTML = html;
        const refreshedDrawer = template.content.querySelector('cart-drawer');
        if (!refreshedDrawer) throw new Error('Cart presentation drawer was not found');

        ['.gbof-cart-reassurance--pre', '.gbof-cart-reassurance--post'].forEach((selector) => {
          const current = drawer.querySelector(selector);
          const refreshed = refreshedDrawer.querySelector(selector);
          if (current && refreshed) current.replaceWith(refreshed.cloneNode(true));
          else if (current && !refreshed) current.remove();
        });

        // The recommendation component remains Shopify-native and is not
        // replaced above. Restore the companion trust-stack class after the
        // refreshed reassurance fragment has been inserted.
        drawer.querySelector('.gbof-cart-trust')?.classList.add('gbof-cart-trust--stacked');
        preloadRecommendations(drawer);
        instrumentRecommendations(drawer);
        refreshNativeMobileScrollLayer(drawer);
      } catch (error) {
        // A failed presentation refresh must never block Canopy's already
        // completed quantity change, cart drawer, or checkout hand-off.
        console.warn('[GBOF cart] presentation refresh after quantity change failed', error);
      }
    }, 0);
  }

  function preloadRecommendations(drawer) {
    const root = drawer?.querySelector('#cart-recommendations');
    const sourceProductId = recommendationSourceProductId(root);
    if (!root || !sourceProductId || root.dataset.gbofRecommendationsPreloaded === 'true') return;

    // Native ProductRecommendations waits until its below-fold element enters
    // the viewport. In a cart drawer, that creates a late network response and
    // a large content-height change while a shopper is already thumb-scrolling.
    // Start the same Shopify recommendation request as soon as the cart section
    // is refreshed, rather than waiting for a scroll gesture to reveal it.
    root.dataset.gbofRecommendationsPreloaded = 'true';
    root.dataset.gbofSourceProductId = sourceProductId;
    root.dataset.gbofPreloadScrollHeight = String(drawer.querySelector('.drawer__content')?.scrollHeight || 0);
    root.removeAttribute('data-product-id');
    const url = root.dataset.url;
    if (!url) return;

    fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`Recommendation response ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (!drawer.contains(root)) return;
        const template = document.createElement('template');
        template.innerHTML = html;
        const fetchedRoot = template.content.querySelector('product-recommendations');
        if (!fetchedRoot?.hasChildNodes()) return;
        root.innerHTML = fetchedRoot.innerHTML;
        root.dataset.gbofRecommendationsReady = 'true';
        window.initLazyImages?.();
      })
      .catch((error) => {
        // Recommendations are optional. A failed preload must never interfere
        // with scrolling, add-to-cart, checkout, or the existing cart contents.
        console.warn('[GBOF cart] recommendation preload failed', error);
      });
  }

  function instrumentRecommendations(drawer) {
    const root = drawer?.querySelector('#cart-recommendations');
    const list = root?.querySelector('ul[role="list"]');
    const fallback = drawer?.querySelector('.gbof-cart-trust');
    if (!root || !list || !fallback || root.dataset.gbofProductFirst === 'true') return false;

    // A product choice should never be interrupted by a proof card. Keep the
    // add-ons together as one vertical "pizza night" flow, then place all GBOF
    // reassurance below it as a single easy-to-scan stack.
    const products = [...list.children].filter((element) => element.matches('li'));
    if (!products.length) return false;

    root.classList.add('gbof-cart-upsell-flow');
    root.querySelector('carousel-slider')?.setAttribute('disable-mobile', 'true');
    const heading = root.querySelector('h2');
    if (heading) heading.textContent = 'Create the perfect pizza night';
    const header = root.querySelector('carousel-slider > .mb-2');
    if (header && !root.querySelector('.gbof-cart-upsell-flow__intro')) {
      const intro = document.createElement('p');
      intro.className = 'gbof-cart-upsell-flow__intro';
      intro.textContent = 'Forgotten anything? Add a pizza-night essential in one tap.';
      header.after(intro);
    }

    products.forEach((product, index) => {
      const button = product.querySelector('button[name="add"], button.js-quick-add');
      if (button) {
        const directAdd = !!button.closest('form[action="/cart/add"]') && !button.classList.contains('js-quick-add');
        button.classList.remove('btn--icon');
        button.classList.add('gbof-cart-upsell-add');
        button.innerHTML = directAdd ? '<span aria-hidden="true">+</span> Add' : 'Choose options';
        button.setAttribute('aria-label', directAdd ? 'Add to pizza night' : 'Choose options for pizza night');
      }
      const link = product.querySelector('a[href*="/products/"]');
      const handle = productHandleFromHref(link?.href);
      if (handle) {
        publish('upsell_viewed', {
          ...cartContext(drawer),
          recommendation_position: index + 1,
          recommendation_handle: handle,
          source_product_id: recommendationSourceProductId(root)
        });
      }
    });

    fallback.classList.remove('gbof-cart-trust--interleaved');
    fallback.classList.add('gbof-cart-trust--stacked');
    root.dataset.gbofRecommendationsReady = 'true';
    root.dataset.gbofProductFirst = 'true';
    const beforePreloadHeight = Number(root.dataset.gbofPreloadScrollHeight || 0);
    const afterPreloadHeight = drawer.querySelector('.drawer__content')?.scrollHeight || 0;
    if (!beforePreloadHeight || Math.abs(afterPreloadHeight - beforePreloadHeight) > 16) {
      refreshNativeMobileScrollLayer(drawer);
    }

    return true;
  }

  function observeDrawer(drawer) {
    if (!drawer || drawer.dataset.gbofCartAnalyticsReady === 'true') return;
    drawer.dataset.gbofCartAnalyticsReady = 'true';
    let lastCartSignature = cartItemSignature(drawer);

    observer = new MutationObserver(() => {
      const nextCartSignature = cartItemSignature(drawer);
      if (nextCartSignature !== lastCartSignature) {
        lastCartSignature = nextCartSignature;
        refreshCartPresentation(drawer);
      }
      preloadRecommendations(drawer);
      instrumentRecommendations(drawer);
      const isOpen = drawer.getAttribute('aria-hidden') === 'false';
      if (isOpen && !lastDrawerOpen) {
        drawerOpenSequence += 1;
        publish('drawer_viewed', cartContext(drawer));
      }
      lastDrawerOpen = isOpen;
    });
    observer.observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });

    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const recommendation = target.closest('#cart-recommendations');
      if (recommendation) {
        const productCard = target.closest('li');
        const link = productCard?.querySelector('a[href*="/products/"]');
        const handle = productHandleFromHref(link?.href);
        if (handle) {
          pendingUpsell = {
            handle,
            sourceProductId: recommendationSourceProductId(recommendation),
            position: recommendationPosition(productCard),
            expiresAt: Date.now() + 15000
          };
          publish('upsell_clicked', {
            ...cartContext(drawer),
            recommendation_handle: handle,
            recommendation_position: recommendationPosition(productCard),
            source_product_id: recommendationSourceProductId(recommendation)
          });
        }
      }

      if (target.closest('button[name="checkout"]')) {
        publish('checkout_clicked', cartContext(drawer));
      }
    }, true);

    document.addEventListener('on:cart:add', (event) => {
      const source = event.target instanceof Element ? event.target : null;
      const recommendation = source?.closest('#cart-recommendations');
      const attributedUpsell = recommendation ? {
        sourceProductId: recommendationSourceProductId(recommendation),
        handle: null,
        position: null
      } : (pendingUpsell && pendingUpsell.expiresAt > Date.now() ? pendingUpsell : null);
      if (!attributedUpsell) return;
      publish('upsell_added', {
        ...cartContext(drawer),
        added_variant_id: event.detail?.variantId || null,
        recommendation_handle: attributedUpsell.handle || null,
        recommendation_position: attributedUpsell.position || null,
        source_product_id: attributedUpsell.sourceProductId || null
      });
      pendingUpsell = undefined;
    });

    document.addEventListener('on:line-item:change', (event) => {
      const source = event.target instanceof Element ? event.target : null;
      // Canopy dispatches this after replacing its native cart fragments. The
      // event target can already be detached at this point, so the signature
      // guard above is the primary path and this schedules the same harmless,
      // debounced presentation read as an immediate fallback.
      if (!source || source.closest('cart-drawer') === drawer || !source.isConnected) {
        refreshCartPresentation(drawer);
      }
    });

    preloadRecommendations(drawer);
    instrumentRecommendations(drawer);
    const isOpen = drawer.getAttribute('aria-hidden') === 'false';
    if (isOpen && !lastDrawerOpen) {
      drawerOpenSequence += 1;
      publish('drawer_viewed', cartContext(drawer));
    }
    lastDrawerOpen = isOpen;
  }

  function init() {
    observeDrawer(document.getElementById('cart-drawer'));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
